import {captureTokenAuthError, sweetAPI} from 'app/store/sweet-api';
import {showMessages} from 'app/store/sweet-api/utils';
import {
  APP_SETTINGS_LANGUAGES_URL,
  APP_SETTINGS_TIMEZONES_URL,
  APP_SETTINGS_URL,
} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {SETTINGSTAGS} from 'app/utils/rtk-tags';

import type {OptionType} from './types';
import type {
  AccountSettingsSerializedType,
  ListResponseType,
} from 'sweet-types';

const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      SETTINGSTAGS.TAGS,
      SETTINGSTAGS.TIMEZONES,
      SETTINGSTAGS.LANGUAGES,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getAccountSettings: build.query<AccountSettingsSerializedType, void>({
        query: () => APP_SETTINGS_URL,
        onQueryStarted: async (args, {queryFulfilled, ...api}) => {
          queryFulfilled.catch((result) => {
            captureTokenAuthError(result.error as any, api, [
              'required-not-in-scope',
            ]);
          });
        },
        providesTags: () => [{type: SETTINGSTAGS.TAGS, id: 'list'}],
      }),
      timezoneSettings: build.query<
        ListResponseType<OptionType>,
        {page: string}
      >({
        query: () => APP_SETTINGS_TIMEZONES_URL,
        providesTags: () => [{type: SETTINGSTAGS.TIMEZONES, id: 'list'}],
      }),
      languageSettings: build.query<
        ListResponseType<OptionType>,
        {page: string}
      >({
        query: () => APP_SETTINGS_LANGUAGES_URL,
        providesTags: () => [{type: SETTINGSTAGS.LANGUAGES, id: 'list'}],
      }),

      updateAccountSettings: build.mutation<
        AccountSettingsSerializedType,
        {
          account_id: number;
        } & Partial<
          Pick<
            AccountSettingsSerializedType,
            | 'company_name'
            | 'company_logo'
            | 'industry_classification'
            | 'is_2fa_required'
            | 'require_ai_data_sharing_at'
            | 'can_export'
            | 'can_edit_tags'
            | 'can_see_all_tasks'
            | 'can_assign_tasks'
          >
        >
      >({
        query: ({account_id, ...body}) => ({
          url: `${APP_SETTINGS_URL}${account_id}/`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: () => [{type: SETTINGSTAGS.TAGS, id: 'list'}],
        onQueryStarted: showMessages({
          onSuccess: () => gt('Account settings updated'),
          onError: () => gt('Unable to update account settings.'),
        }),
      }),
    }),
  });

// subscribe/fetch - to be used by class components
export const subscribeToAccountSettings =
  extendedAPI.endpoints.getAccountSettings.initiate;

// read cached data - to be used by useSelector / class components
export const accountSettingsSelector =
  extendedAPI.endpoints.getAccountSettings.select();

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useQuery: useAccountSettings,
  useLazyQuery: useLazyAccountSettings,
  useQueryState: useAccountSettingsState,
} = extendedAPI.endpoints.getAccountSettings;

export const {
  useUpdateAccountSettingsMutation: useUpdateAccountSettings,
  useLanguageSettingsQuery: useLanguageSettings,
  useTimezoneSettingsQuery: useTimezoneSettings,
} = extendedAPI;
